import type { MetaFunction } from "react-router";
import { marketingLinks, MarketingShell, socialImageMeta } from "../components/marketing/layout";
import { ArrowRight } from "../components/marketing/icons";
import { SITE_ORIGIN } from "../brand";

const URL_ = `${SITE_ORIGIN}/shopify-b2b-quote-workflow-without-plus`;
const TITLE = "Shopify B2B Quote Workflow Without Plus — Guide";
const DESCRIPTION =
  "Learn how to run negotiated Shopify B2B quotes with buyer links, draft-order acceptance, and PO PDF parsing without Shopify Plus.";

export const links = marketingLinks;

export const meta: MetaFunction = () => [
  { title: TITLE },
  { name: "description", content: DESCRIPTION },
  {
    name: "keywords",
    content:
      "Shopify B2B without Plus, B2B quote workflow Shopify, negotiated per-quote pricing Shopify, PO PDF parser Shopify, draftOrder B2B Shopify",
  },
  { name: "robots", content: "index, follow" },
  { tagName: "link", rel: "canonical", href: URL_ },
  { property: "og:title", content: TITLE },
  { property: "og:description", content: DESCRIPTION },
  { property: "og:url", content: URL_ },
  { property: "og:type", content: "article" },
  ...socialImageMeta(),
  { name: "twitter:title", content: TITLE },
  { name: "twitter:description", content: DESCRIPTION },
  {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "TechArticle",
      headline: TITLE,
      description: DESCRIPTION,
      url: URL_,
      datePublished: "2026-06-06",
      dateModified: "2026-06-06",
      author: { "@type": "Organization", name: "Legitimate LLC" },
      publisher: {
        "@type": "Organization",
        name: "Legitimate LLC",
        url: SITE_ORIGIN,
      },
      about: [
        "Shopify B2B without Plus",
        "B2B quote workflow Shopify",
        "draftOrderCreate B2B",
        "Negotiated per-quote pricing Shopify",
        "PO PDF parsing Shopify",
        "Shopify Plus alternative",
      ],
    },
  },
  {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: "How to Run a Shopify B2B Quote Workflow Without Shopify Plus",
      description:
        "Stand up a B2B quote-and-accept workflow on Shopify Basic, Grow, or Advanced. Negotiated per-quote pricing, public quote links, real Shopify Orders on accept.",
      totalTime: "PT15M",
      tool: [{ "@type": "HowToTool", name: "AI B2B Quote Desk" }],
      step: [
        { "@type": "HowToStep", name: "Install Quote Desk", text: "Install AI B2B Quote Desk from the Shopify App Store. Free 14-day Starter trial." },
        { "@type": "HowToStep", name: "Set negotiated per-quote pricing", text: "Build a quote and set the unit price on each line as you negotiate. The line and quote totals recompute automatically." },
        { "@type": "HowToStep", name: "Add company profiles", text: "Add B2B customer profiles. Multiple buyers per company, address book, payment-term preferences." },
        { "@type": "HowToStep", name: "Build your first quote", text: "Pick the company, add line items, set negotiated per-line pricing, and set a quote-valid-until date." },
        { "@type": "HowToStep", name: "Send the quote", text: "Click Send. We generate a public quote link (/q/<random-token>). Share via email, Slack, or any channel." },
        { "@type": "HowToStep", name: "Customer accepts", text: "The customer opens the link in a browser. No Shopify account required. They click Accept." },
        { "@type": "HowToStep", name: "Real Shopify Order is created", text: "We call Shopify's draftOrderCreate GraphQL mutation. A real Shopify Order is created from the quote line items." },
        { "@type": "HowToStep", name: "Customer pays via Shopify checkout", text: "The customer pays through your normal Shopify checkout — credit card, ACH, anything you accept. Order flows into Shopify admin." },
        { "@type": "HowToStep", name: "Upgrade for PO PDF parsing + AI counter-offers", text: "On Pro+ ($49/mo), drop incoming PO PDFs into the dashboard for auto-parsed draft quotes, and use the AI counter-offer engine for negotiations." },
      ],
    },
  },
];

export default function ShopifyB2bQuoteWorkflowGuide() {
  return (
    <MarketingShell>
      <article className="qd-section qd-article">
        <div className="qd-article__meta qd-reveal" data-d="1">
          <span>Guide</span>
          <span>9 min read</span>
          <span>2026-06-06</span>
        </div>
        <h1 className="qd-reveal" data-d="1">
          How to run a Shopify B2B quote workflow without Shopify Plus
        </h1>
        <p className="qd-article__standfirst qd-reveal" data-d="2">
          Shopify Plus B2B is excellent above $5M/year in B2B revenue — and it
          costs $2,300/month minimum. For most wholesale merchants under that
          scale, the real B2B surface is the quote workflow. Here's how to run
          it on the Shopify plan you already have.
        </p>

        <div className="qd-prose">
          <p>
            Shopify Plus B2B gives you a fully separate wholesale storefront,
            branded wholesale checkout, customer portals with repeat ordering,
            and 200+ checkout customizations. For distributors, small
            wholesalers, custom-manufacturer shops, and niche brands selling to
            other brands, most of that is overhead. The real B2B surface area is
            the <strong>quote workflow</strong>: a buyer sends a request or PO,
            you build a quote, you share a link, the buyer accepts, you fulfill.
          </p>
          <p>
            This guide walks through standing up a professional B2B quote
            workflow on Shopify Basic, Grow, or Advanced — using a third-party
            app for the quote layer — at a thirtieth of the cost of Plus.
          </p>

          <h2>What you're building</h2>
          <p>The end state is a workflow where:</p>
          <ol>
            <li>A B2B buyer sends a request or a PO.</li>
            <li>You build a quote in the Shopify admin with negotiated per-line pricing.</li>
            <li>You click Send. A public quote link is generated.</li>
            <li>You share the link by email, Slack, or any channel.</li>
            <li>The buyer opens the link in a browser — no Shopify account needed.</li>
            <li>The buyer clicks Accept.</li>
            <li>
              A real Shopify order is created via the <code>draftOrderCreate</code>{" "}
              GraphQL mutation.
            </li>
            <li>The buyer pays through your normal Shopify checkout.</li>
            <li>The order shows up in your Shopify admin like any other order.</li>
          </ol>
          <p>All of this works on Shopify Basic, Grow, and Advanced. No Plus required.</p>

          <h2>Step 1 — Install the quote workflow app</h2>
          <p>
            Several Shopify apps handle the quote layer. We'll use{" "}
            <a href="https://apps.shopify.com/quote-desk">AI B2B Quote Desk</a>{" "}
            as the example because it's the lowest-priced full-feature option on
            the Shopify App Store at $29/month Starter (14-day free trial).
            Alternatives include B2B Wholesale Club (browse-and-checkout
            focused), SparkLayer (full B2B portal), and BSS Commerce B2B
            (wholesale storefront focused) at $19–249/month depending on feature
            coverage. See the <a href="/compare">honest comparison</a> for which
            fits your B2B flow.
          </p>

          <h2>Step 2 — Set negotiated per-quote pricing</h2>
          <p>
            The first thing that differentiates a B2B workflow from a retail one
            is per-deal pricing. Acme gets the box at $1.09 instead of $1.40 on
            a 500-unit run. Beta negotiates a flat per-unit price on a key SKU.
            Gamma gets net-30 terms.
          </p>
          <p>
            In Quote Desk, you set the unit price directly on each line as you
            build the quote. Adjust a line, and the line total and quote total
            recompute as you negotiate:
          </p>
          <ul>
            <li>
              <strong>Per line</strong> — set the negotiated unit price on any
              line item in the quote.
            </li>
            <li>
              <strong>Per quantity</strong> — price the line to the volume the
              buyer is committing to on this order.
            </li>
            <li>
              <strong>Per deal</strong> — the prices live on the quote, so each
              negotiation stands on its own and is logged in the quote ledger.
            </li>
          </ul>

          <h2>Step 3 — Add company profiles</h2>
          <p>
            Most B2B buyers are companies with multiple people. Acme has Sue at
            HQ and Bob at the warehouse. Both send POs. Set up the company once
            with its contacts and ship-to locations — pulled from your Shopify
            companies — and the company context comes with every quote you build
            for any of its buyers.
          </p>

          <h2>Step 4 — Build your first quote</h2>
          <ol>
            <li>From the dashboard, click New Quote.</li>
            <li>Pick the company. The buyer auto-selects if there's only one.</li>
            <li>
              Add line items — search your Shopify catalog, set quantities, and
              set the negotiated unit price per line.
            </li>
            <li>Set a Quote Valid Until date (typical: 30 days).</li>
            <li>Add notes if needed (delivery date, shipping, special terms).</li>
            <li>Click Save Draft to keep working, or Send to push it out.</li>
          </ol>

          <h2>Step 5 — Send the quote and share the link</h2>
          <p>
            When you click Send, the app generates a public URL — something like{" "}
            <code>https://your-quote-app.example/q/abc123xyz</code> with an
            opaque random token. The token doesn't reveal buyer identity or
            order value; only people who have the URL can open the quote. Share
            it by:
          </p>
          <ul>
            <li>
              <strong>Email</strong> — paste the link into your reply to the
              buyer's request or PO.
            </li>
            <li>
              <strong>Slack</strong> — drop it in a shared B2B channel.
            </li>
            <li>
              <strong>A PDF</strong> — generate a branded quote PDF with the
              link in the footer and attach it.
            </li>
          </ul>

          <h2>Step 6 — The buyer accepts</h2>
          <p>
            The buyer opens the link in a browser and sees a clean quote page —
            your branding, the line items, the negotiated prices, the total,
            your terms, and an Accept button. They click Accept (or Decline, or
            let it expire on the Valid Until date).
          </p>

          <h2>Step 7 — A real Shopify order is created</h2>
          <p>
            Behind the scenes, the app calls Shopify's{" "}
            <code>draftOrderCreate</code> mutation with the line items from the
            quote. Shopify creates the order in your admin, the buyer is
            invoiced through your normal checkout, and the order flows into
            Orders, fulfillment, and reporting — the same as any other order.
          </p>

          <h2>Step 8 — Upgrade for PO PDF parsing + AI counter-offers</h2>
          <p>
            Starter covers the full quote workflow. If your B2B is heavy on
            inbound POs, Pro ($49/mo) adds PO PDF parsing — drop an incoming PO
            PDF and the parser extracts line items, quantities, and prices and
            matches them to your catalog with a confidence score per line. A
            15-minute manual flow becomes a 60-second review-and-send.
          </p>
          <p>
            Growth ($79/mo) adds an AI counter-offer engine: when a buyer
            counters, the engine reads that company's order history and your
            margin floor and drafts a reasonable counter-counter. You approve,
            edit, or override before it sends. Growth also includes the
            verification harness, which replays your pricing rules against past
            orders so you can prove totals still match before a change.
          </p>

          <h2>How it compares to Shopify Plus B2B</h2>
          <p>
            Plus's B2B suite adds a separate wholesale storefront with B2B-only
            product visibility, customer portals with repeat ordering, branded
            wholesale checkout, and 200+ checkout customizations. If you need any
            of those, Plus is the right pick. If you just need the quote
            workflow — and most under-$5M-B2B merchants do — a third-party app on
            your existing tier delivers the same outcome at a thirtieth of the
            platform cost.
          </p>

          <h2>Frequently asked questions</h2>
          <h3>Do I really not need Shopify Plus?</h3>
          <p>
            For most B2B Shopify merchants under $5M/year in B2B revenue, no. The
            quote workflow is the main B2B surface; the rest of Plus is overhead
            most under-$5M merchants don't need. Above $5M, Plus's base price
            gets diluted and the full suite makes more sense.
          </p>
          <h3>How does the buyer accept without a Shopify account?</h3>
          <p>
            The public quote URL (/q/&lt;token&gt;) is a stateless page anyone
            with the URL can view. When the buyer clicks Accept, the app uses
            your Shopify Admin API access to create the order on their behalf,
            then invoices them through your normal checkout. The buyer never
            authenticates with Shopify directly.
          </p>
          <h3>What happens if a buyer counter-offers?</h3>
          <p>
            You can edit and resend the quote — the quote ledger keeps the full
            revision history — or use the AI counter-offer engine on Growth to
            get a margin-aware suggestion in seconds. You always approve before
            sending.
          </p>
          <h3>Is this PCI compliant?</h3>
          <p>
            Yes. The quote app never touches credit card numbers. Payment is
            handled by Shopify's checkout, which is PCI-DSS Level 1 certified.
          </p>
          <h3>Can I export quotes for accounting?</h3>
          <p>
            Yes. Export quotes to CSV — filtered by status, company, or date —
            including line items, company, total, status, send date, and accept
            date. Drop it into your accounting software.
          </p>

          <div className="qd-callout">
            <p>
              <strong>Next step.</strong>{" "}
              <a href="https://apps.shopify.com/quote-desk">
                Install AI B2B Quote Desk on the Shopify App Store
              </a>{" "}
              · <a href="/pricing">see pricing</a> ·{" "}
              <a href="/features">the full feature list</a> ·{" "}
              <a href="/compare">vs Shopify Plus B2B and other B2B apps</a>.
            </p>
          </div>
        </div>
      </article>

      <section className="qd-section qd-band">
        <div className="qd-wrap">
          <div className="qd-cta qd-reveal" data-d="1">
            <h2>Run a Plus-grade B2B quote workflow on any Shopify plan</h2>
            <p>
              Quotes, PO PDF parsing, and AI counter-offers — at a thirtieth of
              the price of Shopify Plus B2B. Every plan ships with a 14-day free
              trial.
            </p>
            <div className="qd-cta__actions">
              <a className="qd-btn" href="https://apps.shopify.com/quote-desk">
                See it on the App Store
                <ArrowRight />
              </a>
              <a className="qd-btn qd-btn--ghost" href="/">
                Back to Quote Desk
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
